<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import MembershipsDemoStage from '~/components/marketing/MembershipsDemoStage.vue';
import {COMMISSION_PERCENT} from '~/utils/features';

// Memberships and passes: the club's membership list, run from the club page
// and the app. The page runs the real membership components on demo data,
// because the feature is what players and the club see.
definePageMeta({
  name: 'FeaturesMemberships',
  path: '/features/memberships',
});

const {setMeta} = useHeadService();
const {accountCtaLabel} = useMarketingCta();

setMeta(
    'Club membership management: memberships and passes | Tap Leagues',
    `Run your club's memberships and passes online. Players get and pay for them from the club page or the app, renewals happen automatically, families, juniors, applications and health questions handled. Any competition can need one or cost less with one. ${COMMISSION_PERCENT}% plus Stripe fees, only on payments taken through Tap Leagues.`,
);

const KINDS = [
  {icon: 'mdi-autorenew', name: 'Ongoing', blurb: 'Renews every term from the day it starts: a year, a quarter, a month. The classic Senior or Junior membership.'},
  {icon: 'mdi-calendar-range-outline', name: 'Set dates', blurb: 'One term with a start and an end, on sale from a date you choose. A season pass.'},
  {icon: 'mdi-repeat', name: 'Repeating blocks', blurb: 'Blocks that share the same dates and roll on by themselves. An 8-session pass that covers a meet\'s sessions.'},
  {icon: 'mdi-account-group-outline', name: 'Family or group', blurb: 'One purchase and one price for several people, with places that have their own ages. The buyer names everyone; each person gets their own.'},
  {icon: 'mdi-account-plus-outline', name: 'Introductory', blurb: 'For people new to the club. After the first term it renews as another type, such as Senior.'},
  {icon: 'mdi-human-male-child', name: 'Linked to a child', blurb: 'A parent\'s membership that goes with a child\'s. It lasts only while the child\'s does.'},
];

const FAQS = [
  {q: 'How do players get a membership?', a: 'From the club page on the website or in the app. They choose a type, answer any questions you ask, say who pays, and pay by card. Depending on the type they are in straight away or you approve them first.'},
  {q: 'What about juniors?', a: 'An under-18 applying for themselves names a parent or carer, who agrees by email and can pay. Under-13s have a parent give the health consent. The club has to be verified by Tap Leagues before it can offer under-18 memberships.'},
  {q: 'Can someone else pay?', a: 'Yes. A parent, a partner or a sponsor pays by email link, with no account. They get receipts, renewal reminders and a page to change the card or stop paying.'},
  {q: 'How do renewals work?', a: 'A membership set to renew automatically is charged when the term ends, with a reminder 3, 7 or 14 days before by the length of the term. Members can switch renewal off, cancel at the end of the term, or cancel inside the 14-day cooling-off period with a refund.'},
  {q: 'Can we move our current members over?', a: 'Yes. Import a CSV or Excel file from ClubSpark, Spond, Pitchero or your own spreadsheet, up to 2,000 rows. It creates the people, the types and each membership with its dates and status, nobody is charged, and you can undo for 7 days.'},
  {q: 'Can a competition be for members only?', a: 'Yes. Any ladder, box league, tournament, americano league or meet can need a membership or pass of a type you tick, or offer a different entry price to holders. Bookings have their own row per membership too.'},
  {q: 'Who sees health answers?', a: 'Only club admins you give the "Can see health details" permission to, and every view and download is logged. Health answers are kept apart from everything else and are never in a list download or an import.'},
  {q: 'What does it cost?', a: `Nothing to use. ${COMMISSION_PERCENT}% plus Stripe's fees comes out of each payment taken through Tap Leagues. A membership paid in cash or by bank transfer is recorded with nothing taken.`},
];

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {'@type': 'Answer', text: faq.a},
      })),
    }),
  }],
});
</script>

<template>
  <base-page>
    <!-- ===== HERO ===== -->
    <section class="fmb-hero">
      <div class="fmb-hero-glow" aria-hidden="true"/>
      <v-container class="fmb-wrap fmb-hero-inner">
        <NuxtLink to="/features" class="fmb-back"><v-icon size="16" icon="mdi-arrow-left"/> All features</NuxtLink>
        <span class="fmb-eyebrow">Memberships and passes</span>
        <h1 class="fmb-title">Your club's memberships, run from the club page</h1>
        <p class="fmb-lead">
          Memberships that renew, passes for a block of sessions. Players get and pay for them from your club page or
          the app, renewals happen by themselves, and any competition or booking can need one, or cost less with one.
          Families, juniors, applications you approve, health questions and a spreadsheet import are all handled.
        </p>
        <div class="fmb-hero-cta">
          <marketing-cta-btn color="primary" size="large">{{ accountCtaLabel }}</marketing-cta-btn>
          <v-btn variant="outlined" color="primary" size="large" to="/features/bookings">Playing area bookings</v-btn>
        </div>
        <p class="fmb-note">Included with Tap Leagues, free.* <commission-note/></p>
      </v-container>
    </section>

    <v-container class="fmb-wrap">
      <!-- ===== THE DEMO ===== -->
      <section class="fmb-demo" aria-labelledby="fmb-demo-title">
        <h2 id="fmb-demo-title" class="fmb-h2">This is it, running</h2>
        <p class="fmb-sub">
          A made-up club, the real screens otherwise. Open a membership, switch its renewal, then look at what the
          club sees.
        </p>
        <memberships-demo-stage class="fmb-stage"/>
      </section>

      <!-- ===== KINDS ===== -->
      <section class="fmb-section" aria-labelledby="fmb-kinds-title">
        <h2 id="fmb-kinds-title" class="fmb-h2">Any kind of membership</h2>
        <p class="fmb-sub">Start from Senior, Junior, Social, Family, Introductory, Parent Play or Season pass, or from blank. Each one is a membership or a pass; only the words players see change.</p>
        <div class="fmb-kinds tl-card" role="list">
          <div v-for="kind in KINDS" :key="kind.name" class="fmb-kind" role="listitem">
            <span class="fmb-kind-icon"><v-icon size="18" :icon="kind.icon"/></span>
            <h3 class="fmb-kind-name">{{ kind.name }}</h3>
            <p class="fmb-kind-blurb">{{ kind.blurb }}</p>
          </div>
        </div>
      </section>

      <!-- ===== HOW IT WORKS ===== -->
      <section class="fmb-section" aria-labelledby="fmb-how-title">
        <h2 id="fmb-how-title" class="fmb-h2">Setting it up</h2>
        <ol class="fmb-steps">
          <li class="tl-card">
            <span class="fmb-step-n">1</span>
            <h3>Create a membership or pass</h3>
            <p>Under Memberships &amp; passes in the club's admin area, or describe it to the <NuxtLink to="/features/setup-assistant">setup assistant</NuxtLink>. A name, who can have it, the price and how long it lasts. Instalments, a joining fee, your questions and terms when you want them.</p>
          </li>
          <li class="tl-card">
            <span class="fmb-step-n">2</span>
            <h3>Make it live</h3>
            <p>Live goes on the club page for people to get. Live, hidden works by link only. Closed to new keeps current holders renewing. A paid type goes live once the club's payment account is.</p>
          </li>
          <li class="tl-card">
            <span class="fmb-step-n">3</span>
            <h3>Players get it, you watch it run</h3>
            <p>Applications to approve, members and pass holders with renewal dates, failed payments retried, a list to download. Record a cash payment, change a type, cancel or refund from the same page.</p>
          </li>
        </ol>
      </section>

      <!-- ===== DETAILS ===== -->
      <section class="fmb-section" aria-labelledby="fmb-details-title">
        <h2 id="fmb-details-title" class="fmb-h2">What else it does</h2>
        <ul class="fmb-details">
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Pay all at once, monthly or quarterly. A joining fee taken once, never at renewal. Every amount shown to the penny.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Approve each application, or let people straight in. A paid application holds the card and charges when you approve.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Juniors who age out are told before their term ends and offered the next type. Nothing is charged until they apply.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Your own terms or a Tap Leagues template, agreed on applying and again at renewal when they change.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Health questions as a separate step with its own consent, visible only to admins you tick, every view logged.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Keep the player list, club chat and name search for people with a membership, with a grace period for everyone else.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>A pass can cover a meet's sessions: pass holders are in free, everyone else pays the session fee.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fmb-check"/>Put your live memberships and passes on your own website with a widget. Getting one and paying happen on Tap Leagues.</li>
        </ul>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="fmb-section" aria-labelledby="fmb-faq-title">
        <h2 id="fmb-faq-title" class="fmb-h2">Questions</h2>
        <div class="fmb-faqs">
          <div v-for="faq in FAQS" :key="faq.q" class="fmb-faq tl-card">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="fmb-final">
        <div class="fmb-final-streaks" aria-hidden="true"/>
        <div class="fmb-final-inner">
          <h2 class="fmb-final-title">Put the membership list online this week</h2>
          <p class="fmb-final-sub">Set up the club, create a membership, share the club page.</p>
          <div class="fmb-final-cta">
            <marketing-cta-btn color="white" size="large" class="text-primary">{{ accountCtaLabel }}</marketing-cta-btn>
            <v-btn variant="outlined" color="white" size="large" to="/features">All features</v-btn>
          </div>
        </div>
      </section>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.fmb-wrap {
  max-width: 1100px;
}

.fmb-hero {
  position: relative;
  overflow: hidden;
  background:
      radial-gradient(circle at 14% 0%, rgba(211, 41, 70, 0.08), transparent 46%),
      linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, rgb(var(--v-theme-light)) 100%);
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.fmb-hero-inner {
  padding-top: 32px;
  padding-bottom: 36px;

  @media (min-width: 960px) {
    padding-top: 44px;
    padding-bottom: 48px;
  }
}

.fmb-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  text-decoration: none;
  margin-bottom: 18px;
}

.fmb-eyebrow {
  display: block;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 12px;
}

.fmb-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.05;
  max-width: 760px;
}

.fmb-lead {
  font-size: 16px;
  line-height: 1.7;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 720px;
  margin-top: 14px;
}

.fmb-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.fmb-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 10px;
}

.fmb-h2 {
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.fmb-sub {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;
  max-width: 640px;
}

.fmb-demo {
  margin-top: 40px;

  @media (min-width: 960px) {
    margin-top: 56px;
  }
}

.fmb-stage {
  margin-top: 20px;
}

.fmb-section {
  margin-top: 48px;

  @media (min-width: 960px) {
    margin-top: 64px;
  }
}

/* Kinds: a compact list */
.fmb-kinds {
  margin-top: 18px;
  overflow: hidden;
}

.fmb-kind {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 2px 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  @media (min-width: 760px) {
    grid-template-columns: 32px minmax(200px, 30%) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    padding: 11px 16px;
  }
}

.fmb-kind-icon {
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));

  @media (min-width: 760px) {
    grid-row: auto;
  }
}

.fmb-kind-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin: 0;
}

.fmb-kind-blurb {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
  margin: 0;
}

/* Steps */
.fmb-steps {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: grid;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(3, 1fr);
  }

  li {
    padding: 18px;
  }

  h3 {
    font-size: 15px;
    font-weight: 700;
    margin-top: 10px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fmb-step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
}

.fmb-details {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: grid;
  gap: 10px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px 24px;
  }

  li {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.55;
  }
}

.fmb-check {
  color: rgb(var(--v-theme-success));
  margin-top: 2px;
  flex: none;
}

.fmb-faqs {
  display: grid;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.fmb-faq {
  padding: 18px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fmb-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 64px;
  margin-bottom: 24px;
  padding: 56px 24px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 88px;
    padding: 72px 40px;
  }
}

.fmb-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.fmb-final-inner {
  position: relative;
  z-index: 1;
}

.fmb-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.fmb-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.fmb-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}
</style>
